import { type GuidanceTier, guidanceTier } from "@/lib/guidance-tier";
import type { PlanLineShareView, PlanLineView } from "@/schemas/resource-plans";

/** A plan line's hours cell is a decimal string; blank or non-numeric counts as 0. */
function parseHours(hours: string): number {
  const n = Number(hours);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Pure: total plan hours per Resource per month, summed across every line. A line's hours
 * belong to its Suggested Resource — or, on a split line (RP-24), to each Share holder for
 * the hours of their own Share. Lines naming nobody are skipped. Outer key = resourceId,
 * inner key = month.
 */
export function planHoursByResourceMonth(
  lines: ReadonlyArray<
    Pick<PlanLineView, "suggestedResourceId" | "months"> & {
      shares?: ReadonlyArray<Pick<PlanLineShareView, "resourceId" | "months">>;
    }
  >,
): Map<string, Map<string, number>> {
  const byResource = new Map<string, Map<string, number>>();
  const add = (resourceId: string, cells: readonly { month: string; hours: string }[]) => {
    let byMonth = byResource.get(resourceId);
    if (byMonth === undefined) {
      byMonth = new Map<string, number>();
      byResource.set(resourceId, byMonth);
    }
    for (const cell of cells) {
      byMonth.set(cell.month, (byMonth.get(cell.month) ?? 0) + parseHours(cell.hours));
    }
  };
  for (const line of lines) {
    const shares = line.shares ?? [];
    if (shares.length > 0) {
      for (const share of shares) add(share.resourceId, share.months);
      continue;
    }
    if (line.suggestedResourceId !== null) add(line.suggestedResourceId, line.months);
  }
  return byResource;
}

export interface ProjectedLoadInput {
  guidanceHours: number | null;
  capacityHours: number | null;
  planHours: number;
  /** When false, the bar shows existing guidance only — this plan's hours are not added. */
  includesPlanHours: boolean;
  /**
   * Hours reserved for ANOTHER Account this month (docs/specs/resource-reservation.md) — a
   * reservation spends the Resource's open time, so it counts as load here. 0 when
   * unreserved, reserved for this plan's own Account, or the reservation has ended. null = unknown
   * (no guidance/capacity) and counts as 0: it never pushes the total up on its own, and
   * the missing guidance/capacity already makes the tier `none`.
   */
  reservedHours: number | null;
  /** The Account the reservation is for, so the tooltip can name it. Ignored when nothing is reserved. */
  reservedForAccountName?: string | null;
}

export interface ProjectedLoad {
  tier: GuidanceTier;
  /** Guidance hours from other work; null = no guidance data. */
  otherHours: number | null;
  planHours: number;
  /** Hours reserved for another Account this month; 0 when none (or unknown). */
  reservedHours: number;
  /** The Account those hours are reserved for, when known. */
  reservedForAccountName: string | null;
  /** null when there is no guidance data. */
  totalHours: number | null;
  capacityHours: number | null;
  /** Rounded percent of capacity; null when unknown. */
  pct: number | null;
  /** Echoed from the input so the tooltip knows whether `planHours` is in the total. */
  includesPlanHours: boolean;
}

/**
 * Pure: a suggested Resource's projected monthly load. With no guidance data the tier is
 * `none` even if plan hours exist — we never paint a confident colour on half the picture.
 */
export function projectedLoad(input: ProjectedLoadInput): ProjectedLoad {
  const { guidanceHours, capacityHours, planHours, includesPlanHours } = input;
  const reservedHours = input.reservedHours ?? 0;
  const totalHours =
    guidanceHours === null
      ? null
      : guidanceHours + (includesPlanHours ? planHours : 0) + reservedHours;
  const tier = guidanceTier(totalHours, capacityHours);
  const pct =
    totalHours === null || capacityHours === null || capacityHours <= 0
      ? null
      : Math.round((totalHours / capacityHours) * 100);
  return {
    tier,
    otherHours: guidanceHours,
    planHours,
    reservedHours,
    reservedForAccountName: input.reservedForAccountName ?? null,
    totalHours,
    capacityHours,
    pct,
    includesPlanHours,
  };
}

/** Hours with at most one decimal and no trailing ".0" (136 → "136h", 12.25 → "12.3h"). */
function formatHours(hours: number): string {
  return `${Math.round(hours * 10) / 10}h`;
}

/** Pure: hover text for a month's load bar. */
export function loadTooltip(monthLabel: string, load: ProjectedLoad): string {
  const { tier, otherHours, totalHours, capacityHours, pct } = load;
  if (
    tier === "none" ||
    otherHours === null ||
    totalHours === null ||
    capacityHours === null ||
    pct === null
  ) {
    return `${monthLabel}: No guidance data for this month`;
  }
  const capacity = `of ${formatHours(capacityHours)} capacity (${pct}%)`;
  // Terms in the order they stack up: existing work, then the reservation, then this plan.
  const terms = [`${formatHours(otherHours)} ${load.includesPlanHours ? "other work" : "guided"}`];
  if (load.reservedHours > 0) {
    const account = load.reservedForAccountName ?? "another Account";
    terms.push(`${formatHours(load.reservedHours)} reserved for ${account}`);
  }
  if (load.includesPlanHours) terms.push(`${formatHours(load.planHours)} this plan`);
  // A lone term is the whole total; the "= total" tail only earns its place when summing.
  const sum =
    terms.length > 1 ? `${terms.join(" + ")} = ${formatHours(totalHours)}` : terms.join("");
  return `${monthLabel}: ${sum} ${capacity}`;
}
